import React, { useState, useEffect } from 'react';
import './UserDashboard.css';
import Navbar from '../components/Navbar';
import { fetchOverviewData } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const GROUP_LABELS = {
  3: 'Duke Leads',
  11: 'Tax Check Pro',
  12: 'CFB Tax',
};

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'alltime', label: 'All time' },
];

const Overview = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('today');
  const { isAdmin } = useAuth();

  const loadData = async (selectedPeriod) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchOverviewData(selectedPeriod);
      setOverviewData(data);
    } catch (err) {
      setError('Failed to load overview data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(period);
  }, [period]);

  return (
    <div className='user-dashboard'>
      <Navbar isAdmin={isAdmin} />
      <div className='dashboard-container dashboard-container--no-sidebar'>
        <main className='dashboard-main'>
          <div className='dashboard-section'>
            <div className='overview-header'>
              <div>
                <h2>Overview</h2>
                <p className='section-description'>
                  Welcome to the Overview page. Here you can find group usage data, including usage
                  and transaction counts for each service.
                  <br />
                  <br />
                  All transactions start from the beginning of 2026.
                </p>
                <p className=''></p>
              </div>
              <select
                className='overview-period-select'
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {loading && (
              <div className='empty-state'>
                <div className='empty-icon'>⏳</div>
                <p>Loading overview data...</p>
              </div>
            )}
            {error && (
              <div className='empty-state'>
                <div className='empty-icon'>⚠️</div>
                <p className='overview-error'>{error}</p>
              </div>
            )}
            {!loading &&
              !error &&
              overviewData &&
              Object.entries(overviewData).map(([groupId, rows], idx) => {
                const totalCredits = rows.reduce((sum, r) => sum + r.credits_consumed, 0);
                const totalTransactions = rows.reduce((sum, r) => sum + r.transaction_count, 0);
                const hasPrice = rows.some((r) => r.price_per_transaction != null);

                return (
                  <React.Fragment key={groupId}>
                    {idx > 0 && <div className='section-divider' />}
                    <div className='overview-group'>
                      <div className='overview-content'>
                        <h3>{GROUP_LABELS[groupId] || `Group ${groupId}`}</h3>
                        <div className='overview-table-divider' />
                        {rows.length === 0 ? (
                          <div className='empty-state'>
                            <div className='empty-icon'>📊</div>
                            <p>No usage recorded for this group.</p>
                            <p className='empty-hint'>
                              Usage data will appear here once transactions are recorded.
                            </p>
                          </div>
                        ) : (
                          <table className='overview-table'>
                            <thead>
                              <tr>
                                <th>Service</th>
                                <th>Usage (£)</th>
                                {hasPrice && <th>Price per Transaction (£)</th>}
                                <th>Transactions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row) => (
                                <tr key={row.service}>
                                  <td>{row.service}</td>
                                  <td>£{row.credits_consumed.toFixed(2)}</td>
                                  {hasPrice && (
                                    <td>
                                      {row.price_per_transaction != null
                                        ? `£${row.price_per_transaction.toFixed(2)}`
                                        : '—'}
                                    </td>
                                  )}
                                  <td>{row.transaction_count}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className='overview-totals-row'>
                                <td>Total</td>
                                <td>£{totalCredits.toFixed(2)}</td>
                                {hasPrice && <td></td>}
                                <td>{totalTransactions}</td>
                              </tr>
                            </tfoot>
                          </table>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Overview;
